import React, { useState } from 'react';
import PageHero from '../components/site/PageHero';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from '../hooks/use-toast';
import { ArrowRight, Sparkles, Quote } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const teamVoices = [
  {
    name: 'Anam Fatima',
    role: 'Sr. Manager',
    image: 'https://ewenrealtors.com/assets/images/anam-fatima.jpeg',
    quote: 'Ewen Realtors has given me a runway to grow as a professional — both personally and in my craft. Every week here has been genuinely rewarding.',
  },
  {
    name: 'Abhishek Arya',
    role: 'Full-Stack Developer',
    image: '/abhishek.png',
    quote: 'What surprised me the most was the culture — everyone is quick to help, share, and teach. It makes coming in on a Monday something to look forward to.',
  },
  {
    name: 'Talia Khalid',
    role: 'Content Writer',
    image: 'https://ewenrealtors.com/assets/images/talia-khalid.jpg',
    quote: 'If you are looking for a dynamic place with real opportunities to build a career you can be proud of, this is that place.',
  },
];

const FIELDS = [
  { name: 'username', label: 'Full Name', required: true, colSpan: 1 },
  { name: 'email', label: 'Email Address', required: true, type: 'email', colSpan: 1 },
  { name: 'phone', label: 'Phone Number', required: true, colSpan: 1 },
  { name: 'previous_job', label: 'Previous Job Title', required: true, colSpan: 1 },
  { name: 'address', label: 'Street Address', required: true, colSpan: 2 },
  { name: 'city', label: 'City', required: true, colSpan: 1 },
  { name: 'state', label: 'State / Province', required: true, colSpan: 1 },
  { name: 'zip', label: 'ZIP / Postal Code', required: true, colSpan: 1 },
  { name: 'country', label: 'Country', required: true, colSpan: 1 },
  { name: 'resume', label: 'Drop Your Resume Link Here', required: true, colSpan: 2, placeholder: 'https://drive.google.com/... or LinkedIn URL' },
];

export default function Careers() {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const missing = FIELDS.filter((f) => f.required && !form[f.name]).map((f) => f.label);
    if (missing.length) {
      toast({ title: 'Please complete the form', description: `Missing: ${missing.slice(0, 3).join(', ')}${missing.length > 3 ? '…' : ''}` });
      return;
    }
    try {
      setBusy(true);
      await axios.post(`${API}/career-applications`, form);
      toast({ title: 'Application received', description: 'Thank you! Our HR team will get back within 5 working days.' });
      setForm({});
    } catch (err) {
      toast({ title: 'Something went wrong', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setBusy(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div>
      <PageHero
        eyebrow="Work With Us"
        title="Join the Ewen family, and grow with us."
        subtitle="We're hiring. Come build the next chapter of Uttarakhand's most trusted real-estate story."
        image="/gallery_2025/WhatsApp Image 2024-04-08 at 11.08.53 AM (1).jpeg"
      />

            {/* Team voices */}
            <section className="bg-[#f6f1e7] py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-[#8a6b3a]" />
              <span className="text-[#8a6b3a] text-xs uppercase tracking-[0.3em]">Voices from the Team</span>
              <div className="h-px w-12 bg-[#8a6b3a]" />
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0f1a14] leading-[1.1]">People make the place.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {teamVoices.map((t) => (
              <article key={t.name} className="bg-white border border-[#0f1a14]/10 p-8 hover:border-[#8a6b3a] transition-colors">
                <div className="h-24 w-24 rounded-full overflow-hidden bg-[#0f1a14]">
                  <img src={t.image} alt={t.name} className="h-full w-full object-cover" />
                </div>
                <Quote className="h-6 w-6 text-[#c9a56a] mt-6" />
                <p className="text-[#0f1a14]/80 mt-3 leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-6 pt-6 border-t border-[#0f1a14]/10">
                  <div className="font-serif text-xl text-[#0f1a14]">{t.name}</div>
                  <div className="text-xs uppercase tracking-widest text-[#8a6b3a] mt-1">{t.role}</div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* About the company */}
      <section className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5">
            <div className="relative aspect-[3/4] overflow-hidden">
              <img src="/gallery_2025/IMG20250817184256.jpg" alt="About Ewen Realtors" className="h-full w-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-12 bg-[#8a6b3a]" />
              <span className="text-[#8a6b3a] text-xs uppercase tracking-[0.3em]">About Ewen Realtors</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0f1a14] leading-[1.1]">
              Defining Dreams — since 2011.
            </h2>
            <div className="mt-8 space-y-5 text-[#0f1a14]/75 leading-relaxed text-lg">
              <p>
                Ewen Realtors has been serving families across Uttarakhand for more than a decade. What started as a small setup is now an established corporate with one guiding mission — Defining Dreams.
              </p>
              <p>
                Every day, our team walks buyers through the entire home-buying process with transparency, warmth and complete honesty. That commitment is what has made us one of the region&apos;s most trusted names.
              </p>
              <p>
                Today, we are building <span className="text-[#8a6b3a] font-medium">The Ewen Park</span> near Majkhali, Ranikhet — a signature township with two sub-communities:
                <span className="text-[#8a6b3a] font-medium"> The Ewen Village</span> and
                <span className="text-[#8a6b3a] font-medium"> The Ewen Studio</span> — a mix of residential villas, cottages and studio homes across the hills.
              </p>
            </div>
          </div>
        </div>
      </section>

            {/* Intro / hero image + why */}
            <section className="bg-[#f6f1e7] py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-12 bg-[#8a6b3a]" />
              <span className="text-[#8a6b3a] text-xs uppercase tracking-[0.3em]">We&apos;re Hiring!</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0f1a14] leading-[1.1]">
              A place for young, ambitious, and imaginative minds.
            </h2>
            <div className="relative mt-8 pl-8 border-l-2 border-[#c9a56a]">
              <Quote className="h-6 w-6 text-[#c9a56a] absolute -left-3 -top-2 bg-[#f6f1e7]" />
              <p className="font-serif text-xl md:text-2xl text-[#0f1a14] italic leading-snug">
                All of us do not have equal talent. But all of us have an equal opportunity to develop it.
              </p>
            </div>
            <p className="text-[#0f1a14]/70 mt-6 leading-relaxed text-lg">
              At Ewen Realtors, we look for people who bring energy, empathy, and a hunger to learn. If that sounds like you, we would love to meet.
            </p>
            <Button onClick={scrollToForm} className="mt-8 bg-[#0f1a14] text-white hover:bg-[#1c2e26] rounded-none px-8 py-6 tracking-wider">
              Join Now! <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative aspect-[4/3] overflow-hidden">
              <img src="https://ewenrealtors.com/assets/images/careers.png" alt="Ewen Realtors team" className="h-full w-full object-cover" />
              <div className="absolute bottom-4 left-4 bg-[#c9a56a] text-[#0f1a14] px-4 py-2 text-xs uppercase tracking-widest">
                Team since 2011
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Career at Ewen */}
      <section className="bg-[#0f1a14] py-24">
        <div className="mx-auto max-w-5xl px-6 lg:px-10 text-center">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="h-px w-12 bg-[#c9a56a]" />
            <span className="text-[#c9a56a] text-xs uppercase tracking-[0.3em]">Career at Ewen Realtors</span>
            <div className="h-px w-12 bg-[#c9a56a]" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-white leading-[1.1]">
            <em className="not-italic text-[#c9a56a]">Small aim is a crime;</em> have a great aim.
          </h2>
          <p className="text-white/75 mt-6 leading-relaxed text-lg max-w-3xl mx-auto">
            We support our team members and give them the room to grow with the company. Along with a competitive compensation package, we invest in strong ethics, honest feedback, and a genuinely collaborative culture. If you bring imagination and curiosity, we&apos;ll bring the runway.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-14">
            {[
              { t: 'Learning Budget', d: 'For books, courses and conferences.' },
              { t: 'Health Coverage', d: 'For you and your immediate family.' },
              { t: 'Hill Retreats', d: 'Team offsites at our own townships.' },
              { t: 'Growth Roadmap', d: 'Bi-annual reviews and clear career paths.' },
            ].map((p) => (
              <div key={p.t} className="p-6 border border-white/10 hover:border-[#c9a56a] transition-colors text-left">
                <Sparkles className="h-5 w-5 text-[#c9a56a]" />
                <div className="font-serif text-xl text-white mt-3">{p.t}</div>
                <div className="text-white/60 text-sm mt-2 leading-relaxed">{p.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section id="apply" className="bg-white py-24 scroll-mt-24">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-[#8a6b3a]" />
              <span className="text-[#8a6b3a] text-xs uppercase tracking-[0.3em]">Upload Your Resume</span>
              <div className="h-px w-12 bg-[#8a6b3a]" />
            </div>
            <h2 className="font-serif text-4xl md:text-5xl text-[#0f1a14] leading-[1.1]">Ready to join us? Tell us about you.</h2>
            <p className="text-[#0f1a14]/70 mt-5 leading-relaxed">
              Send us your details and a resume link. We read every application and typically get back within five working days.
            </p>
          </div>

          <form onSubmit={submit} className="mt-14 bg-[#f6f1e7] p-8 md:p-12 border border-[#0f1a14]/10">
            <div className="grid md:grid-cols-2 gap-5">
              {FIELDS.map((f) => (
                <div key={f.name} className={f.colSpan === 2 ? 'md:col-span-2' : ''}>
                  <label className="text-xs uppercase tracking-widest text-[#8a6b3a]">
                    {f.label}{f.required ? ' *' : ''}
                  </label>
                  <Input
                    type={f.type || 'text'}
                    placeholder={f.placeholder || ''}
                    value={form[f.name] || ''}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="mt-2 bg-white border-[#0f1a14]/15 rounded-none h-12"
                  />
                </div>
              ))}
            </div>
            <Button type="submit" disabled={busy} className="mt-8 bg-[#0f1a14] text-white hover:bg-[#1c2e26] rounded-none px-10 py-6 tracking-wider">
              {busy ? 'Sending...' : 'Send Request'}
              {!busy && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>
            <p className="text-xs text-[#0f1a14]/50 mt-4">
              By submitting, you agree to be contacted by our HR team about relevant openings.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}
